import { useMemo } from "react";
import { CardHeaderTag } from "components/shared/Tags/StatusTags";
import { getPoStatusDisplayConfig } from "../poUtils";
import { PO_STATUS } from "../../../../utils/constants/others";
import { CalendarDays, IndianRupee, MapPin } from "lucide-react";
import { strings } from "utils/constants/strings";
import Table from "components/shared/Table/Table";
import MapLinkButton from "components/shared/MapLinkButton";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import { Loader } from "../../Common/Loader";
import ShowToast from "../../Common/Toast";
import GenerateUpcModal from "../../EmbeddedUpc/GenerateUpcModal";
import { formatAmount, formatDate, PurchaseOrderConfirmationModal, PurchaseOrderSuccessModal } from "./PoDetailsElements";
import PoDisputeDrawer from "./PoDisputeDrawer";
import {
  ActionButton,
  Actions,
  DesktopPage,
  SummaryCard,
  SummaryIcon,
  SummaryItem,
  SummaryValue,
  Title,
  TitleBlock,
  TitleRow,
} from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  CellText,
  DisputeSuggestionText,
  GenerateQrButton,
  ProductImage,
  ProductsCard,
  ProductsTitle,
  StateMessage,
  SummaryLabel,
  SummaryText,
} from "./styles.poDetailsDesktop";

export default function PoDetailsDesktop({
  purchaseOrder,
  items,
  totalWeight,
  isLoading,
  hasError,
  canAccept,
  canDispute,
  canGenerateUpc,
  goToList,
  showConfirmation,
  setShowConfirmation,
  accepting,
  accepted,
  acceptPurchaseOrder,
  showDisputeForm,
  openDispute,
  closeDispute,
  dispute,
  handleGenerateQr,
  checkingComplianceFor,
  upcModalItem,
  closeUpcModal,
  complianceError,
  clearComplianceError,
}) {
  const columns = useMemo(() => {
    const unit = items[0]?.buy_measurement_unit || "unit";
    const base = [
      {
        id: "image",
        header: "Product image",
        meta: { width: 160 },
        cell: (info) => <ProductImage src={info.row.original.product_image} alt="" />,
      },
      {
        id: "name",
        header: "Product name",
        meta: { width: 320 },
        cell: (info) => <CellText>{info.row.original.product_name}</CellText>,
      },
      {
        id: "price",
        header: `Price/${unit}`,
        meta: { width: 140 },
        cell: (info) => {
          const disputeItem = purchaseOrder?.dispute_details?.items?.find(
            (d) => d.purchase_order_item_id === info.row.original.id || d.purchase_order_item_id === info.row.original.purchase_order_item_id
          );
          return (
            <CellText>
              <div>{formatAmount(info.row.original.price_per_unit)}/{info.row.original.buy_measurement_unit}</div>
              {disputeItem && (
                <DisputeSuggestionText>
                  You suggested: {formatAmount(disputeItem.proposed_price)}/{info.row.original.buy_measurement_unit}
                </DisputeSuggestionText>
              )}
            </CellText>
          );
        },
      },
      {
        id: "qty",
        header: "Order qty",
        meta: { width: 140 },
        cell: (info) => (
          <CellText>
            {Number(info.row.original.quantity_ordered || 0).toLocaleString("en-IN")} {info.row.original.buy_measurement_unit}
          </CellText>
        ),
      },
      {
        id: "amount",
        header: "Order amount",
        cell: (info) => (
          <CellText>
            {formatAmount(Number(info.row.original.quantity_ordered || 0) * Number(info.row.original.price_per_unit || 0))}
          </CellText>
        ),
      },
    ];
    if (!canGenerateUpc) return base;
    return [
      ...base,
      {
        id: "action",
        header: "Action",
        meta: { width: 160 },
        cell: (info) => {
          const item = info.row.original;
          if (!item.active_product_variants?.length) return null;
          const checking = checkingComplianceFor === item.product_number;
          return (
            <GenerateQrButton type="button" disabled={checking} aria-busy={checking} onClick={() => handleGenerateQr(item)}>
              {checking ? "Loading..." : "Generate QR"}
            </GenerateQrButton>
          );
        },
      },
    ];
  }, [items, canGenerateUpc, checkingComplianceFor, handleGenerateQr, purchaseOrder]);

  if (isLoading) return <Loader />;
  if (hasError) return <StateMessage>Could not load this purchase order.</StateMessage>;

  const warehouseName = purchaseOrder?.warehouse_code || "—";

  return (
    <DesktopPage>
      <TitleRow>
        <TitleBlock>
          <Breadcrumb
            items={[
              { label: "Home" },
              { label: "Purchase orders", onClick: goToList },
              { label: purchaseOrder.purchase_order_number },
            ]}
          />
          <TitleRow>
            <Title>{purchaseOrder.purchase_order_number}</Title>
            {(() => {
              const statusValue =
                purchaseOrder.po_status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL ||
                purchaseOrder.po_status === PO_STATUS.COMPLETED
                  ? purchaseOrder.po_status
                  : purchaseOrder.derived_status || purchaseOrder.po_status;
              const { label, bgColor, textColor } = getPoStatusDisplayConfig(statusValue);
              return (
                <CardHeaderTag
                  text={label}
                  textColor={textColor}
                  bgColor={bgColor}
                  allSidesRounded
                />
              );
            })()}
          </TitleRow>
        </TitleBlock>
        <Actions>
          {canDispute && <ActionButton type="button" onClick={openDispute}>Raise dispute</ActionButton>}
          {canAccept && (
            <ActionButton type="button" $primary onClick={() => setShowConfirmation(true)}>
              Accept PO
            </ActionButton>
          )}
        </Actions>
      </TitleRow>

      <SummaryCard>
        <SummaryItem>
          <SummaryIcon><CalendarDays size={20} /></SummaryIcon>
          <SummaryText>
            <SummaryLabel>Deliver by</SummaryLabel>
            <SummaryValue>{formatDate(purchaseOrder.expected_delivery_date)}</SummaryValue>
            {purchaseOrder.dispute_details?.proposed_delivery_date && (
              <DisputeSuggestionText>
                You suggested: {formatDate(purchaseOrder.dispute_details.proposed_delivery_date)}
              </DisputeSuggestionText>
            )}
          </SummaryText>
        </SummaryItem>
        <SummaryItem>
          <SummaryIcon><MapPin size={20} /></SummaryIcon>
          <SummaryText>
            <SummaryLabel>Deliver at</SummaryLabel>
            <SummaryValue>
              {warehouseName}
              <MapLinkButton
                latitude={purchaseOrder.warehouse_details?.latitude}
                longitude={purchaseOrder.warehouse_details?.longitude}
                fallbackAddress={purchaseOrder.warehouse_details?.address}
                isMobileView={false}
              />
            </SummaryValue>
          </SummaryText>
        </SummaryItem>
        <SummaryItem>
          <SummaryIcon><IndianRupee size={20} /></SummaryIcon>
          <SummaryText>
            <SummaryLabel>PO amount</SummaryLabel>
            <SummaryValue>{formatAmount(purchaseOrder.po_amount)}</SummaryValue>
          </SummaryText>
        </SummaryItem>
      </SummaryCard>

      <ProductsCard>
        <ProductsTitle>Product details</ProductsTitle>
        <Table
          columns={columns}
          data={items}
          getRowId={(row, index) => String(row.id ?? row.product_number ?? index)}
          emptyMessage="No products found"
        />
      </ProductsCard>

      {showDisputeForm && (
        <PoDisputeDrawer items={items} dispute={dispute} onClose={closeDispute} />
      )}

      <PurchaseOrderConfirmationModal
        visible={showConfirmation && !accepted}
        purchaseOrder={purchaseOrder}
        items={items}
        totalWeight={totalWeight}
        submitting={accepting}
        onBack={() => setShowConfirmation(false)}
        onConfirm={acceptPurchaseOrder}
      />
      <PurchaseOrderSuccessModal visible={accepted} title="PO Accepted" onClose={goToList} />
      <PurchaseOrderSuccessModal visible={dispute.submitted} title={strings.grnModalDisputeRaised} onClose={dispute.onSuccessModalClose} />
      <GenerateUpcModal visible={!!upcModalItem} item={upcModalItem} poId={purchaseOrder.id} onClose={closeUpcModal} />
      {complianceError && <ShowToast errorMsg={complianceError} type="error" onClose={clearComplianceError} />}
    </DesktopPage>
  );
}
